import { useNavigate, useParams } from 'react-router-dom';
import type {
  CampaignActuals,
  CampaignOccurrence,
  MatchedOrderEvidence,
  OccurrenceDetailResponse,
  StoreResponse,
} from '@clarity/contract';
import { useOccurrence } from '../../queries/index.js';
import { useScopeContextOptional } from '../../lib/scopeContext.js';
import { isNotYetOnboarded } from '../../lib/lifecycle.js';
import { dateTime, isOnboardingRequired, money, num } from '../format.js';
import { isAds } from './ProfitModel.js';
import {
  ConfidenceMeter,
  occurrenceOverrides,
  occurrenceWindow,
  type Confidence,
  type OverrideRow,
} from './occurrence-shared.js';

/**
 * OccurrenceDetail (clarity-campaign-matching task 13.4, Req 9.4 / 6.5) — one
 * occurrence's evidence page, rebuilt from the agreed mock
 * `brand/campaigns-mock.html` (VIEW 3 · OCCURRENCE DETAIL) in the teal-glass
 * world. The mock is the contract. It renders:
 *
 *  - the header: the occurrence window + an "Ended" state chip + the one-line
 *    override summary (which term this occurrence changed vs the campaign);
 *  - the OVERRIDE TABLE — campaign default vs this occurrence's resolved term,
 *    with overridden fields highlighted (resolved via the campaign defaults vs
 *    the occurrence's non-null overrides, in `occurrence-shared`);
 *  - the occurrence STATS panel — its own folded actuals (matched orders,
 *    sales, discount cost, and the match confidence), leading with ROAS for ads
 *    or net margin impact for a discount occurrence;
 *  - the matched-orders EVIDENCE table — the orders whose discount signature +
 *    timestamp place them in this occurrence (order / placed / subtotal /
 *    discount / match reason / confidence).
 *
 * It is a sibling route of the CampaignDetail under `store/:storeId`, reached
 * from a PARTICIPATED occurrence row's open (task 13.4). The store comes from
 * the scope context; a passed `store` prop wins (tests mount it outside a
 * ScopeProvider), same pattern as {@link CampaignDetail}.
 */
export function OccurrenceDetail({ store: storeProp }: { store?: StoreResponse }) {
  const { clientId, storeId, cid, oid } = useParams();
  const navigate = useNavigate();

  const scope = useScopeContextOptional();
  const store = storeProp ?? scope?.store ?? null;
  const campaignId = cid ?? '';
  const occurrenceId = oid ?? '';

  const enabled = store != null && !isNotYetOnboarded(store);
  const query = useOccurrence(store?.id ?? '', campaignId, occurrenceId, enabled);
  const data: OccurrenceDetailResponse | null =
    query.data?.status === 200 ? query.data.body : null;

  const back = () => {
    if (clientId && storeId) {
      navigate(`/c/${clientId}/store/${storeId}/campaigns/${campaignId}`);
    }
  };

  if (isOnboardingRequired(query.error)) {
    return (
      <Frame onBack={back}>
        <p className="muted">This branch isn’t onboarded yet.</p>
      </Frame>
    );
  }
  if (query.isLoading) {
    return (
      <Frame onBack={back}>
        <OccurrenceLoading />
      </Frame>
    );
  }
  if (query.isError || !data || !store) {
    return (
      <Frame onBack={back}>
        <p className="error" role="alert">
          Could not load this occurrence.
        </p>
      </Frame>
    );
  }

  const { campaign, occurrence, actuals, evidence } = data;
  const window = occurrenceWindow(occurrence);
  const overrides = occurrenceOverrides(occurrence, campaign);
  const currency = store.profile?.currency ?? 'JOD';

  return (
    <Frame onBack={back} campaignName={campaign.name}>
      <OccurrenceHeader window={window} overrides={overrides} />

      <div className="occ-detail__grid">
        <OverrideTable overrides={overrides} />
        <OccurrenceStats
          actuals={actuals}
          currency={currency}
          ads={isAds(campaign)}
          confidence={occurrenceConfidence(evidence)}
          days={windowDays(occurrence)}
        />
      </div>

      <EvidenceTable evidence={evidence} currency={currency} />
    </Frame>
  );
}

/** The page frame: a Back link to the campaign + an accessible region. */
function Frame({
  onBack,
  campaignName,
  children,
}: {
  onBack: () => void;
  campaignName?: string;
  children: React.ReactNode;
}) {
  return (
    <section
      className="campaigns campaign-detail occ-detail"
      aria-label={campaignName ? `Occurrence — ${campaignName}` : 'Occurrence'}
    >
      <button type="button" className="back" onClick={onBack}>
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="m15 18-6-6 6-6" />
        </svg>
        {campaignName ?? 'Campaign'}
      </button>
      {children}
    </section>
  );
}

/**
 * The occurrence header: the window as the title, an "Ended" state chip, and the
 * one-line override summary (the mock's "cap overridden to 3 JOD (campaign
 * default 4 JOD)"). The summary names every overridden field; when nothing is
 * overridden it says so plainly.
 */
function OccurrenceHeader({
  window,
  overrides,
}: {
  window: ReturnType<typeof occurrenceWindow>;
  overrides: OverrideRow[];
}) {
  const changed = overrides.filter((o) => o.changed);

  return (
    <header className="detail-head occ-detail__head">
      <div>
        <h2 className="detail-head__title">{window.range} occurrence</h2>
        <div className="detail-head__sub">
          <span className="badge occ-chip occ-chip--out">Ended</span>
          <span>{window.month}</span>
          {changed.length > 0 && (
            <span className="occ-detail__override-note">
              ·{' '}
              {changed.map((o, i) => (
                <span key={o.label}>
                  {i > 0 && ', '}
                  <span className="occ-detail__override-hi">
                    {o.label.toLowerCase()} {o.occurrenceValue}
                  </span>{' '}
                  (campaign default {o.campaignValue})
                </span>
              ))}
            </span>
          )}
        </div>
      </div>
    </header>
  );
}

/**
 * The override table (Req 9.4): one row per term, campaign default vs this
 * occurrence's resolved value. Overridden fields are highlighted (the violet
 * `--changed` treatment the mock uses). A real `<table>` with a header row so
 * the three columns announce themselves to assistive tech.
 */
function OverrideTable({ overrides }: { overrides: OverrideRow[] }) {
  return (
    <section className="card panel occ-ledger" aria-label="What this occurrence overrides">
      <h3 className="occ-detail__panel-title">What this occurrence overrides</h3>
      <table className="occ-ledger__table" aria-label="What this occurrence overrides">
        <thead>
          <tr>
            <th scope="col">Field</th>
            <th scope="col">Campaign default</th>
            <th scope="col">This occurrence</th>
          </tr>
        </thead>
        <tbody>
          {overrides.map((o) => (
            <tr key={o.label} className={o.changed ? 'occ-ledger__row--changed' : undefined}>
              <th scope="row" className="occ-ledger__field">
                {o.label}
              </th>
              <td className="occ-ledger__val num">{o.campaignValue}</td>
              <td className={`occ-ledger__val num${o.changed ? ' occ-ledger__val--changed' : ''}`}>
                {o.occurrenceValue}
                {o.changed && <span className="sr-only"> (overridden)</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

/**
 * The occurrence stats panel: its own folded actuals (Req 8.1). Leads with ROAS
 * for an ads occurrence or net margin impact for a discount one (same rule as
 * the list's `roasMeaningful`), then matched orders / sales / discount cost /
 * match confidence as a key-value list.
 */
function OccurrenceStats({
  actuals,
  currency,
  ads,
  confidence,
  days,
}: {
  actuals: CampaignActuals;
  currency: string;
  ads: boolean;
  confidence: Confidence | null;
  days: number | null;
}) {
  const useRoas = ads && actuals.roasMeaningful && actuals.roas !== null;
  const heroValue = useRoas
    ? `${num(roundTo(actuals.roas as number, 1))}×`
    : money(actuals.netMarginImpact, currency);
  const heroCap = useRoas
    ? `ROAS${days ? ` · ${days} day${days === 1 ? '' : 's'}` : ''}`
    : `Net margin impact${days ? ` · ${days} day${days === 1 ? '' : 's'}` : ''}`;
  const heroNeg = !useRoas && actuals.netMarginImpact < 0;

  return (
    <section className="card panel occ-stats" aria-label="This occurrence">
      <h3 className="occ-detail__panel-title">This occurrence</h3>

      <div className="occ-stats__hero">
        <div className={`occ-stats__big num${heroNeg ? ' occ-stats__big--neg' : ''}`}>
          {heroValue}
        </div>
        <div className="occ-stats__cap">{heroCap}</div>
      </div>

      <dl className="occ-stats__kv">
        <Stat k="Matched orders" v={num(actuals.matchedOrders)} />
        <Stat k="Sales" v={money(actuals.sales, currency)} />
        <Stat k="Discount cost" v={money(actuals.discountCost, currency)} />
        {ads && actuals.adSpend !== null && (
          <Stat k="Ad spend" v={money(actuals.adSpend, currency)} />
        )}
        <div className="occ-stats__row">
          <dt className="kv__k">Match confidence</dt>
          <dd className="kv__v occ-stats__conf">
            {confidence ? <ConfidenceMeter level={confidence} /> : <span className="muted">—</span>}
          </dd>
        </div>
      </dl>
    </section>
  );
}

function Stat({ k, v }: { k: string; v: string }) {
  return (
    <div className="occ-stats__row">
      <dt className="kv__k">{k}</dt>
      <dd className="kv__v num">{v}</dd>
    </div>
  );
}

/**
 * The matched-orders evidence table (Req 9.4): the orders whose discount
 * signature + timestamp place them in this occurrence. Columns: order, placed,
 * subtotal, discount, match reason, confidence. Numeric columns use the tabular
 * face; the confidence meter carries its level as text (not color alone).
 */
function EvidenceTable({
  evidence,
  currency,
}: {
  evidence: MatchedOrderEvidence[];
  currency: string;
}) {
  return (
    <section className="card panel occ-evidence" aria-label="Matched orders">
      <h3 className="occ-detail__panel-title">
        Matched orders
        <span className="pm__hint">
          {' '}
          · the evidence — orders whose discount signature and timestamp place them in this
          occurrence
        </span>
      </h3>

      {evidence.length === 0 ? (
        <p className="occ-evidence__empty muted" role="status">
          No matched orders for this occurrence yet.
        </p>
      ) : (
        <div className="occ-evidence__wrap">
          <table className="occ-evidence__table" aria-label="Matched orders evidence">
            <thead>
              <tr>
                <th scope="col">Order</th>
                <th scope="col">Placed</th>
                <th scope="col" className="num-col">
                  Subtotal
                </th>
                <th scope="col" className="num-col">
                  Discount
                </th>
                <th scope="col">Match reason</th>
                <th scope="col">Confidence</th>
              </tr>
            </thead>
            <tbody>
              {evidence.map((row) => (
                <tr key={row.orderId}>
                  <td className="num occ-evidence__id">{row.externalOrderId}</td>
                  <td className="num occ-evidence__when">{dateTime(row.placedAt)}</td>
                  <td className="num-col">{money(row.subtotal, currency)}</td>
                  <td className="num-col">{money(row.discount, currency)}</td>
                  <td className="occ-evidence__reason">{row.reason}</td>
                  <td>
                    <ConfidenceMeter level={row.confidence} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function OccurrenceLoading() {
  return (
    <div className="card campaigns__loading" role="status" aria-busy="true" aria-live="polite">
      <span className="sr-only">Loading occurrence…</span>
      <span className="sk" style={{ inlineSize: '45%' }} aria-hidden="true" />
      <span className="sk" style={{ inlineSize: '80%' }} aria-hidden="true" />
      <span className="sk" style={{ inlineSize: '65%' }} aria-hidden="true" />
    </div>
  );
}

// ── Pure helpers ──────────────────────────────────────────────────────────────

/**
 * The aggregate match confidence for the occurrence, from its evidence rows:
 * the mode (most common) level, biased to the strongest on a tie, so one
 * low-confidence tail order doesn't drag down a strongly-matched run. Null when
 * there is no evidence.
 */
function occurrenceConfidence(evidence: MatchedOrderEvidence[]): Confidence | null {
  if (evidence.length === 0) return null;
  const counts: Record<Confidence, number> = { high: 0, medium: 0, low: 0 };
  for (const row of evidence) counts[row.confidence] += 1;
  const order: Confidence[] = ['high', 'medium', 'low'];
  return order.reduce((best, level) => (counts[level] > counts[best] ? level : best), 'low');
}

/** The occurrence's inclusive day span, for the stats cap ("2 days"). */
function windowDays(occurrence: CampaignOccurrence): number | null {
  const from = new Date(occurrence.startDate);
  const to = new Date(occurrence.endDate);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return null;
  const ms = to.getTime() - from.getTime();
  if (ms < 0) return null;
  // Inclusive of both bounds: a same-day window is 1 day, Sep 8–9 is 2 days.
  return Math.round(ms / (24 * 60 * 60 * 1000)) + 1;
}

function roundTo(value: number, places: number): number {
  const f = 10 ** places;
  return Math.round(value * f) / f;
}
